import { FC, PropsWithChildren, ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Combobox, ComboboxOption } from "@/components/_molecules/combobox/combobox";

import { useGetUnitList } from "../hooks/api/use-get-unit-list";
import { Unit } from "../models/unit-model";

import { UnitSelectDropdownItem } from "./unit-select-dropdown-item";

export interface UnitSelectDropdownProps extends PropsWithChildren {
  workspaceId: string;
  defaultValue?: string | null;
  onSelect?: (unitId: string | null) => void;
  asChild?: boolean;
  isRequired?: boolean;
  modal?: boolean;
}

export const UnitSelectDropdown: FC<UnitSelectDropdownProps> = ({
  children,
  asChild,
  onSelect,
  workspaceId,
  defaultValue,
  isRequired = false,
  modal,
}) => {
  const { t } = useTranslation("common");
  const { unitList } = useGetUnitList({ workspaceId });

  const comboboxOptions = useMemo(() => {
    const options = unitList.map((unit) => ({
      id: unit.id,
      value: `${unit.title} ${unit.symbol ? `(${unit.symbol})` : ""}`,
      original: unit,
    }));

    if (!isRequired) {
      return [
        {
          id: "none",
          value: t("unit.noUnit"),
          original: null,
        },
        ...options,
      ];
    }

    return options;
  }, [unitList, isRequired, t]);

  const renderItem = (option: ComboboxOption<Unit>): ReactElement => {
    if (option.id === "none") {
      return <UnitSelectDropdownItem title={t("unit.noUnit")} selected={defaultValue === null} />;
    }

    const unit = option.original;

    if (!unit) {
      return <></>;
    }

    const selected = defaultValue === option.id;
    return <UnitSelectDropdownItem title={unit.title} symbol={unit.symbol} selected={selected} />;
  };

  const handleUserSelected = (selectedOption: ComboboxOption<Unit> | null) => {
    if (!selectedOption || !onSelect) {
      return;
    }

    if (selectedOption.id === "none") {
      onSelect(null);
      return;
    }

    onSelect(selectedOption.id);
  };

  if (!comboboxOptions.length) {
    return <></>;
  }

  return (
    <Combobox
      asChild={asChild}
      options={comboboxOptions}
      renderItem={renderItem}
      onSelect={handleUserSelected}
      placeholder={t("unit.unitPlaceholder")}
      side="bottom"
      align="start"
      modal={modal}
    >
      {children}
    </Combobox>
  );
};
